import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function CreditsPage({ user, dark, onTopup, onUseCredit }) {
  const [walletBalance, setWalletBalance] = useState(0);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);

  const c = {
    bg: dark ? '#0d0d0d' : '#f8f8f8',
    card: dark ? '#1c1c1c' : '#ffffff',
    cardBorder: dark ? '#2e2e2e' : '#e8e8e8',
    text: dark ? '#eeeeee' : '#111111',
    sub: dark ? '#555555' : '#aaaaaa',
    barBg: dark ? '#252525' : '#f0f0f0',
  };

  useEffect(() => {
    axios.get(`${BACKEND}/credits?lineUserId=${user.userId}`)
      .then(res => {
        setWalletBalance(res.data.wallet_balance || 0);
        setCredits(res.data.credits || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  function getExpiryInfo(expiresAt) {
    if (!expiresAt) return null;
    const now = new Date();
    const exp = new Date(expiresAt);
    const diffMs = exp - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'หมดอายุแล้วค่ะ', color: '#f87171', expired: true };
    if (diffDays <= 30) return { label: `หมดอายุใน ${diffDays} วันค่ะ`, color: '#f59e0b', expired: false };
    return {
      label: `หมดอายุ ${exp.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })}`,
      color: dark ? '#555' : '#aaa',
      expired: false
    };
  }

  const handleUseCredit = async (credit) => {
    try {
      const res = await axios.get(`${BACKEND}/services?platform=${encodeURIComponent(credit.services.platform)}`);
      const fullService = res.data.find(s => s.id === credit.service_id);
      if (fullService && onUseCredit) onUseCredit(fullService);
    } catch {}
  };

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'200px'}}>
      <p style={{color:'#aaa',fontSize:'13px',fontFamily:'Prompt,sans-serif'}}>กำลังโหลดค่ะ...</p>
    </div>
  );

  return (
    <div style={{padding:'12px',display:'flex',flexDirection:'column',gap:'8px',background:c.bg,minHeight:'100vh',fontFamily:'Prompt,sans-serif'}}>

      <p style={{fontSize:'15px',fontWeight:'700',color:c.text,margin:'4px 0 8px'}}>เครดิตของคุณค่ะ</p>

      <div style={{background:'linear-gradient(135deg,#FF4500,#ff6a00)',borderRadius:'14px',padding:'16px 18px',marginBottom:'4px'}}>
        <p style={{fontSize:'11px',color:'rgba(255,255,255,0.85)',margin:'0 0 4px'}}>ยอดเงินคงเหลือ</p>
        <p style={{fontSize:'26px',fontWeight:'700',color:'#fff',margin:0}}>฿{walletBalance.toFixed(2)}</p>
        <p style={{fontSize:'9.5px',color:'rgba(255,255,255,0.75)',margin:'4px 0 0'}}>ใช้ซื้อแพ็กของบริการไหนก็ได้ค่ะ</p>
      </div>

      <button
        onClick={onTopup}
        style={{width:'100%',padding:'12px',border:'1px solid #FF4500',borderRadius:'10px',background:dark?'#1a0800':'#fff5f0',cursor:'pointer',fontFamily:'Prompt,sans-serif',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}
      >
        <i className="ti ti-qrcode" style={{fontSize:'16px',color:'#FF4500'}} aria-hidden="true"/>
        <span style={{fontSize:'12px',fontWeight:'600',color:'#FF4500'}}>เติมเครดิต</span>
      </button>

      <p style={{fontSize:'12px',fontWeight:'600',color:c.sub,margin:'8px 0 2px'}}>แพ็กที่ซื้อไว้</p>

      {credits.length === 0 ? (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'30px 0',gap:'8px'}}>
          <i className="ti ti-package" style={{fontSize:'36px',color:'#555'}} aria-hidden="true"/>
          <p style={{color:'#555',fontSize:'12px'}}>ยังไม่มีแพ็กที่ซื้อไว้ค่ะ</p>
        </div>
      ) : (
        credits.map(credit => {
          const unitLabel = credit.services?.unit_label || 'หน่วย';
          const isAutoLike = credit.services?.type === 'Subscriptions';
          const balance = Number(credit.balance) || 0;
          const pct = Math.min(100, (balance / 500) * 100);
          const expiryInfo = isAutoLike ? null : getExpiryInfo(credit.expires_at);

          return (
            <div
              key={credit.id}
              style={{
                background: expiryInfo?.expired ? (dark ? '#1a0a0a' : '#fff5f5') : c.card,
                border: `1px solid ${expiryInfo?.expired ? '#f87171' : expiryInfo?.color && expiryInfo.color === '#f59e0b' ? '#f59e0b' : c.cardBorder}`,
                borderRadius:'12px',
                padding:'12px 14px'
              }}
            >
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'8px'}}>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:'12px',fontWeight:'600',color:c.text,margin:0}}>{credit.services?.name}</p>
                  <p style={{fontSize:'10px',color:c.sub,margin:'2px 0 0'}}>{credit.services?.platform}</p>
                </div>
                <p style={{
                  fontSize:'15px',fontWeight:'700',
                  color: expiryInfo?.expired ? '#f87171' : '#FF4500',
                  margin:0,whiteSpace:'nowrap',marginLeft:'8px'
                }}>
                  {balance.toLocaleString()} {isAutoLike ? 'โพสต์' : unitLabel}
                </p>
              </div>

              <div style={{background:c.barBg,borderRadius:'4px',height:'4px',marginBottom:'6px'}}>
                <div style={{height:'100%',borderRadius:'4px',background: expiryInfo?.expired ? '#f87171' : '#FF4500',width:`${pct}%`}}/>
              </div>

              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                {expiryInfo ? (
                  <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
                    <i className={`ti ${expiryInfo.expired ? 'ti-clock-off' : 'ti-clock'}`} style={{fontSize:'10px',color:expiryInfo.color}} aria-hidden="true"/>
                    <p style={{fontSize:'9.5px',color:expiryInfo.color,margin:0,fontWeight: expiryInfo.expired || expiryInfo.color === '#f59e0b' ? '600' : '400'}}>
                      {expiryInfo.label}
                    </p>
                  </div>
                ) : <div/>}

                {!isAutoLike && !expiryInfo?.expired && (
                  <button
                    onClick={() => handleUseCredit(credit)}
                    style={{background:'#FF4500',border:'none',borderRadius:'6px',padding:'5px 12px',cursor:'pointer',fontFamily:'Prompt,sans-serif'}}
                  >
                    <span style={{fontSize:'9px',fontWeight:'600',color:'#fff'}}>ใช้เครดิต</span>
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

    </div>
  );
}

export default CreditsPage;
